import { useMemo, type ReactNode } from "react";
import {
  DEPARTAMENTOS,
  FECHA_CORTE_MUNICIPIOS,
  FUENTE_DEPARTAMENTOS,
  FUENTE_MUNICIPIOS,
  MUNICIPIOS_SAMPLE,
  NODOS_ANALITICOS,
  NOTA_NODO_ANALITICO,
  SEDE_REFERENCIA_ENVIGADO,
  capacidadPractica,
  capacidadTeorica,
  coberturaUmbralesEstandar,
  sensibilidadCapacidad,
  FACTORES_OCUPACION,
  type Municipio,
  type NodoAnalitico,
  type ParametrosCapacidad,
  type RegionColombia,
} from "../../../data/colombia";
import { EvidenceTag } from "../../../evidence/EvidenceTag";
import { useHotspotStore } from "../../state/hotspotSlice";
import { useJourneyStore } from "../../state/journeyMachine";
import { idMunicipio, idNodo, idSede, useNationalStore, type CapaNacional, type NivelAcceso } from "./nationalStore";

/** Sala de referencia del panel de capacidad [DISEÑO salvo sesionesDia NORMATIVO]. */
const PARAMS_CAPACIDAD: ParametrosCapacidad = {
  salas: 1, // [DISEÑO] sala de referencia
  puestosPorSala: 12, // [DISEÑO]
  sesionesDia: 10, // [NORMATIVO Anexo 60: sesiones de 90 min en jornada de 16 h]
  diasHabiles: 250, // [DISEÑO] días hábiles del periodo anual
};

const SIN_DATO = "sin dato — [POR VERIFICAR]";
const REGIONES: readonly RegionColombia[] = ["Andina", "Caribe", "Pacífica", "Orinoquía", "Amazonía"];
const REGION_POR_DEPTO = new Map(DEPARTAMENTOS.map((d) => [d.codigoDivipola, d.region]));
const NOMBRE_MUNI = new Map(MUNICIPIOS_SAMPLE.map((m) => [m.codigoDivipola, m.nombre]));

const fmt = new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 });

export function NationalPanel() {
  return (
    <div className="flex flex-col gap-4">
      <Leyenda />
      <CapasToggles />
      <Filtros />
      <FichaSeleccion />
      <ListaAccesible />
      <CapacidadCard />
      <NotaTerritorial />
    </div>
  );
}

/* ── Leyenda permanente ─────────────────────────────────────────────── */

function Leyenda() {
  return (
    <section aria-label="Leyenda del mapa" className="rounded-md border border-[var(--cale-border)] bg-[var(--cale-surface)] p-3">
      <h3 className="text-sm font-semibold">Leyenda</h3>
      <ul className="mt-1.5 flex flex-col gap-1 text-xs">
        <li className="flex items-center gap-2">
          <span aria-hidden className="inline-block h-3 w-3 rounded-full" style={{ background: "#2f6394" }} />
          Municipio (muestra de 61)
        </li>
        <li className="flex items-start gap-2">
          <span aria-hidden className="mt-0.5 inline-block h-3 w-3 rotate-45" style={{ background: "#b9772a" }} />
          <span>
            Nodo analítico{" "}
            <em className="text-[var(--cale-ink-muted)]">(≠ sede física)</em>
          </span>
        </li>
        <li className="flex items-center gap-2">
          <span aria-hidden className="inline-block h-0 w-3 border-x-[6px] border-b-[10px] border-x-transparent" style={{ borderBottomColor: "#186a45" }} />
          Sede candidata [DISEÑO]
        </li>
        <li className="flex items-center gap-2 text-[var(--cale-ink-muted)]">
          <span aria-hidden className="inline-block h-0 w-3 border-x-[6px] border-b-[10px] border-x-transparent border-b-[#8a8a7a]" />
          Categoría prevista — sin registros cargados
        </li>
        <li className="flex items-center gap-2 text-[var(--cale-ink-muted)]">
          <span aria-hidden className="inline-block h-0 w-3 border-x-[6px] border-b-[10px] border-x-transparent border-b-[#4a6a4a]" />
          Categoría prevista — sin registros cargados
        </li>
      </ul>
    </section>
  );
}

/* ── Toggles de capas ───────────────────────────────────────────────── */

const CAPA_LABELS: Record<CapaNacional, string> = {
  municipios: "Municipios (61)",
  nodos: "Nodos analíticos (45)",
  sede: "Sede candidata",
  departamentos: "Departamentos (centroides y nombres) [DISEÑO]",
};

function CapasToggles() {
  const capas = useNationalStore((s) => s.capas);
  const toggleCapa = useNationalStore((s) => s.toggleCapa);
  return (
    <section aria-label="Capas del mapa" className="flex flex-col gap-1.5">
      <h3 className="text-sm font-semibold">Capas</h3>
      {(Object.keys(CAPA_LABELS) as CapaNacional[]).map((capa) => (
        <label key={capa} className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={capas[capa]} onChange={() => toggleCapa(capa)} />
          {CAPA_LABELS[capa]}
        </label>
      ))}
    </section>
  );
}

/* ── Filtros ────────────────────────────────────────────────────────── */

function Filtros() {
  const filtroDepartamento = useNationalStore((s) => s.filtroDepartamento);
  const filtroRegion = useNationalStore((s) => s.filtroRegion);
  const filtroAcceso = useNationalStore((s) => s.filtroAcceso);
  const setFiltroDepartamento = useNationalStore((s) => s.setFiltroDepartamento);
  const setFiltroRegion = useNationalStore((s) => s.setFiltroRegion);
  const setFiltroAcceso = useNationalStore((s) => s.setFiltroAcceso);

  return (
    <section aria-label="Filtros" className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">Filtros</h3>
      <label className="flex flex-col gap-1 text-xs">
        Departamento
        <select
          value={filtroDepartamento ?? ""}
          onChange={(e) => setFiltroDepartamento(e.target.value || null)}
          className="rounded-md border border-[var(--cale-border)] bg-[var(--cale-surface)] px-2 py-1"
        >
          <option value="">Todos</option>
          {DEPARTAMENTOS.map((d) => (
            <option key={d.codigoDivipola} value={d.codigoDivipola}>
              {d.nombre}
            </option>
          ))}
        </select>
      </label>
      <div role="group" aria-label="Filtrar por región" className="flex flex-wrap gap-1">
        {REGIONES.map((r) => (
          <button
            key={r}
            type="button"
            aria-pressed={filtroRegion === r}
            onClick={() => setFiltroRegion(filtroRegion === r ? null : r)}
            className={`rounded-full border px-2.5 py-0.5 text-xs ${
              filtroRegion === r
                ? "border-[var(--cale-accent)] bg-[var(--cale-accent)] text-white"
                : "border-[var(--cale-border)] hover:bg-[var(--cale-surface)]"
            }`}
          >
            {r}
          </button>
        ))}
      </div>
      <div role="radiogroup" aria-label="Nivel de acceso a datos" className="flex gap-1 text-xs">
        {(["todos", "con-dato", "sin-dato"] as NivelAcceso[]).map((nivel) => (
          <label
            key={nivel}
            className={`flex-1 cursor-pointer rounded-md border px-2 py-1 text-center ${
              filtroAcceso === nivel ? "border-[var(--cale-accent)] bg-[var(--cale-accent)] text-white" : "border-[var(--cale-border)]"
            }`}
          >
            <input
              type="radio"
              name="nivel-acceso"
              value={nivel}
              checked={filtroAcceso === nivel}
              onChange={() => setFiltroAcceso(nivel)}
              className="sr-only"
            />
            {nivel === "todos" ? "Todos" : nivel === "con-dato" ? "Con dato" : "Sin dato"}
          </label>
        ))}
      </div>
      <p className="text-xs text-[var(--cale-ink-muted)]">
        Los no coincidentes se atenúan en el mapa (opacidad 0.15). Toda la
        muestra está hoy en nivel «sin dato»: demanda, tiempos de viaje y
        proyecciones son {SIN_DATO}.
      </p>
    </section>
  );
}

/* ── Ficha de selección ─────────────────────────────────────────────── */

function Ficha({ children, titulo, onCerrar }: { children: ReactNode; titulo: string; onCerrar: () => void }) {
  return (
    <article aria-live="polite" className="flex flex-col gap-1.5 rounded-md border border-[var(--cale-border)] bg-[var(--cale-surface)] p-3">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold">{titulo}</h3>
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-md border border-[var(--cale-border)] px-2 py-0.5 text-xs hover:bg-[var(--cale-surface-raised)]"
        >
          Cerrar
        </button>
      </div>
      {children}
    </article>
  );
}

function Fila({ label, children }: { label: string; children: ReactNode }) {
  return (
    <p className="text-xs">
      <span className="font-medium">{label}: </span>
      {children}
    </p>
  );
}

function FichaMunicipio({ municipio }: { municipio: Municipio }) {
  const close = useHotspotStore((s) => s.close);
  return (
    <Ficha titulo={`Municipio — ${municipio.nombre}`} onCerrar={close}>
      <Fila label="Código DIVIPOLA">{municipio.codigoDivipola}</Fila>
      <Fila label="Departamento">{municipio.departamento}</Fila>
      <Fila label="Población (proyección DANE aprox.)">{fmt.format(municipio.poblacion)}</Fila>
      <Fila label="Demanda observada">{SIN_DATO}</Fila>
      <Fila label="Proyección 2030">{SIN_DATO}</Fila>
      <Fila label="Tiempo de viaje al nodo/sede">{SIN_DATO}</Fila>
      <Fila label="Estado probatorio">
        {municipio.estadoProbatorio} <EvidenceTag kind="POR VERIFICAR" />
      </Fila>
      <Fila label="Fuente">{FUENTE_MUNICIPIOS}</Fila>
      <Fila label="Fecha de corte">{FECHA_CORTE_MUNICIPIOS}</Fila>
    </Ficha>
  );
}

function FichaNodo({ nodo }: { nodo: NodoAnalitico }) {
  const close = useHotspotStore((s) => s.close);
  return (
    <Ficha titulo={`${nodo.id} — ${nodo.nombre}`} onCerrar={close}>
      <p className="rounded bg-[var(--cale-surface-raised)] p-2 text-xs">
        {NOTA_NODO_ANALITICO}
      </p>
      <Fila label="Municipio principal (centroide)">
        {NOMBRE_MUNI.get(nodo.municipioPrincipal) ?? nodo.municipioPrincipal} ({nodo.municipioPrincipal})
      </Fila>
      <Fila label="Municipios cubiertos">
        {nodo.municipiosCubiertos
          .map((c) => `${NOMBRE_MUNI.get(c) ?? c} (${c})`)
          .join(", ")}
      </Fila>
      <Fila label="Estado probatorio">
        <EvidenceTag kind="SIMULADO" />
      </Fila>
      <Fila label="Fuente">Zonificación analítica del proyecto sobre {FUENTE_MUNICIPIOS}</Fila>
      <Fila label="Fecha de corte">{FECHA_CORTE_MUNICIPIOS}</Fila>
    </Ficha>
  );
}

function FichaSede() {
  const close = useHotspotStore((s) => s.close);
  const startJourney = useJourneyStore((s) => s.startJourney);
  const sede = SEDE_REFERENCIA_ENVIGADO;
  return (
    <Ficha titulo={sede.nombre} onCerrar={close}>
      <Fila label="Municipio">
        {sede.municipio} ({sede.codigoMunicipio}), {sede.departamento}
      </Fila>
      <Fila label="Estado">
        candidata <EvidenceTag kind="DISEÑO" />
      </Fila>
      <Fila label="Proponente">{sede.proponente}</Fila>
      <p className="text-xs text-[var(--cale-ink-muted)]">{sede.nota}</p>
      <button
        type="button"
        onClick={() => startJourney("sede-envigado")}
        className="self-start rounded-md border border-[var(--cale-accent)] px-2 py-1 text-xs text-[var(--cale-accent)] hover:bg-[var(--cale-surface-raised)]"
      >
        Ver la sede de referencia (Envigado) en modo sede
      </button>
    </Ficha>
  );
}

function FichaSeleccion() {
  const selectedId = useHotspotStore((s) => s.selectedId);
  if (!selectedId) return null;
  if (selectedId.startsWith("m:")) {
    const municipio = MUNICIPIOS_SAMPLE.find((m) => m.codigoDivipola === selectedId.slice(2));
    return municipio ? <FichaMunicipio municipio={municipio} /> : null;
  }
  if (selectedId.startsWith("n:")) {
    const nodo = NODOS_ANALITICOS.find((n) => n.id === selectedId.slice(2));
    return nodo ? <FichaNodo nodo={nodo} /> : null;
  }
  if (selectedId.startsWith("s:")) return <FichaSede />;
  return null;
}

/* ── Lista accesible (equivalente de teclado al clic 3D) ────────────── */

function ListaAccesible() {
  const open = useHotspotStore((s) => s.open);
  const selectedId = useHotspotStore((s) => s.selectedId);
  const filtroDepartamento = useNationalStore((s) => s.filtroDepartamento);
  const filtroRegion = useNationalStore((s) => s.filtroRegion);

  const municipios = useMemo(
    () =>
      MUNICIPIOS_SAMPLE.filter((m) => {
        if (filtroDepartamento && m.codigoDepartamento !== filtroDepartamento) return false;
        if (filtroRegion && REGION_POR_DEPTO.get(m.codigoDepartamento) !== filtroRegion) return false;
        return true;
      }),
    [filtroDepartamento, filtroRegion],
  );
  const nodos = useMemo(
    () =>
      NODOS_ANALITICOS.filter((n) => {
        const depto = n.municipioPrincipal.slice(0, 2);
        if (filtroDepartamento && depto !== filtroDepartamento) return false;
        if (filtroRegion && REGION_POR_DEPTO.get(depto) !== filtroRegion) return false;
        return true;
      }),
    [filtroDepartamento, filtroRegion],
  );

  const btn = (id: string, label: string) => (
    <button
      key={id}
      type="button"
      onClick={() => open(id)}
      aria-pressed={selectedId === id}
      className={`rounded-md border px-2 py-1 text-xs ${
        selectedId === id
          ? "border-[var(--cale-accent)] bg-[var(--cale-accent)] text-white"
          : "border-[var(--cale-border)] hover:bg-[var(--cale-surface)]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <section aria-label="Selección accesible por teclado" className="flex flex-col gap-1.5">
      <h3 className="text-sm font-semibold">Selección por lista</h3>
      <button
        type="button"
        onClick={() => open(idSede(SEDE_REFERENCIA_ENVIGADO.id))}
        className="self-start rounded-md border border-[#186a45] px-2 py-1 text-xs text-[#186a45] hover:bg-[var(--cale-surface)]"
      >
        Sede candidata — Envigado
      </button>
      <details>
        <summary className="cursor-pointer text-xs font-medium">
          Nodos analíticos ({nodos.length})
        </summary>
        <div className="mt-1 flex flex-wrap gap-1">
          {nodos.map((n) => btn(idNodo(n.id), n.id))}
        </div>
      </details>
      <details>
        <summary className="cursor-pointer text-xs font-medium">
          Municipios ({municipios.length})
        </summary>
        <div className="mt-1 flex max-h-40 flex-wrap gap-1 overflow-auto">
          {municipios.map((m) => btn(idMunicipio(m.codigoDivipola), m.nombre))}
        </div>
      </details>
    </section>
  );
}

/* ── Panel de capacidad ─────────────────────────────────────────────── */

function CifraCapacidad({
  label,
  valor,
  detalle,
}: {
  label: string;
  valor: string;
  detalle: string;
}) {
  return (
    <details className="rounded-md border border-[var(--cale-border)] p-2">
      <summary className="cursor-pointer text-xs">
        <span className="font-medium">{label}: </span>
        <span className="font-semibold">{valor}</span>
      </summary>
      <p className="mt-1 text-xs text-[var(--cale-ink-muted)]">{detalle}</p>
    </details>
  );
}

function CapacidadCard() {
  const teorica = capacidadTeorica(PARAMS_CAPACIDAD);
  const practicaMedia = capacidadPractica(PARAMS_CAPACIDAD, FACTORES_OCUPACION.medio);
  const sensibilidad = sensibilidadCapacidad(PARAMS_CAPACIDAD);
  const coberturas = useMemo(() => coberturaUmbralesEstandar(MUNICIPIOS_SAMPLE), []);

  return (
    <section aria-label="Capacidad de referencia" className="flex flex-col gap-1.5 rounded-md border border-[var(--cale-border)] bg-[var(--cale-surface)] p-3">
      <h3 className="text-sm font-semibold">Capacidad de una sala de referencia</h3>
      <p className="text-xs text-[var(--cale-ink-muted)]">
        Parámetros: 1 sala, 12 puestos [DISEÑO], 10 sesiones/día [DISEÑO derivado de sesiones de
        90 min, NORMATIVO Anexo 60], 250 días hábiles [DISEÑO].
      </p>
      <CifraCapacidad
        label="Capacidad teórica"
        valor={`${fmt.format(teorica)} aspirantes/año`}
        detalle="Fórmula: salas × puestosPorSala × sesionesDia × diasHabiles = 1 × 12 × 10 × 250. Unidad: aspirantes por periodo anual. Fuente: modelo del proyecto (capacidad.ts). [DISEÑO] con sesionesDia [NORMATIVO Anexo 60]."
      />
      <CifraCapacidad
        label="Capacidad práctica (escenario medio)"
        valor={`${fmt.format(practicaMedia)} aspirantes/año`}
        detalle={`Fórmula: capacidadTeorica × factorOcupacion (0.775 [DISEÑO]). Unidad: aspirantes por periodo anual. Fuente: modelo del proyecto (capacidad.ts).`}
      />
      <div className="flex flex-col gap-1">
        <p className="text-xs font-medium">Sensibilidad (factor de ocupación [DISEÑO])</p>
        {sensibilidad.map((s) => (
          <CifraCapacidad
            key={s.escenario}
            label={s.escenario.charAt(0).toUpperCase() + s.escenario.slice(1)}
            valor={`${fmt.format(s.capacidadPractica)} aspirantes/año`}
            detalle={`capacidadTeorica × ${s.factorOcupacion}. Fuente: capacidad.ts. [DISEÑO]`}
          />
        ))}
      </div>
      <div className="mt-1 flex flex-col gap-1">
        <p className="text-xs font-medium">Cobertura poblacional (umbrales 60/90/120 min [DISEÑO])</p>
        {coberturas.map((c) => (
          <details key={c.umbralMin} className="rounded-md border border-[var(--cale-border)] p-2">
            <summary className="cursor-pointer text-xs">
              <span className="font-medium">≤ {c.umbralMin} min: </span>
              {Number.isNaN(c.coberturaMunicipiosPct) ? "sin porcentaje (NaN manejado)" : `${c.coberturaMunicipiosPct.toFixed(1)} % de municipios`}
            </summary>
            <p className="mt-1 text-xs text-[var(--cale-ink-muted)]">
              {c.mensaje} Municipios con dato de tiempo: {c.conDato}/{c.total}.
            </p>
          </details>
        ))}
        <EvidenceTag kind="POR VERIFICAR" />
      </div>
    </section>
  );
}

/* ── Nota territorial fija ──────────────────────────────────────────── */

function NotaTerritorial() {
  return (
    <p className="rounded-md border border-[var(--cale-border)] bg-[var(--cale-surface)] p-3 text-xs text-[var(--cale-ink-muted)]">
      Universo oficial DANE/DIVIPOLA: 1.122 municipios (corte 2024) (
      {FUENTE_DEPARTAMENTOS}). Modelo IUE del brief: 1.103 — brecha de 19
      municipios <EvidenceTag kind="POR VERIFICAR" />. Muestra cargada: 61
      municipios representativos (los 33 departamentos están representados).
    </p>
  );
}
